import React, { useRef } from 'react';

import { IMaskMixin } from 'react-imask';

import Input, { SeturInputProps, InputApi } from '@components/Input';
import { useHeight, useLabelTop } from '@components/utils/hooks/useSize';

import { BaseInput, Container } from './styled';

type FormattedInputConfig = {
  blocks: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mask: any;
  shouldPreventChar?: (char: string) => boolean;
  placeholder?: string;
  name?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & { [key: string]: any };

export const FormattedInputBuilder =
  ({
    blocks,
    mask,
    shouldPreventChar = () => false,
    name,
    placeholder,
    ...opts
  }: FormattedInputConfig) =>
  ({
    icon,
    disabled,
    getInputAPI,
    nativeInputRef,
    ...rest
  }: React.PropsWithChildren<
    SeturInputProps & {
      forwardDefaultValue?: string;
      nativeInputRef?: React.MutableRefObject<HTMLInputElement>;
    }
  >) => {
    return (
      <Container disabled={disabled}>
        <FormattedInputWrapper
          mask={mask}
          blocks={blocks}
          unmask={false}
          name={name}
          type='text'
          InputComponent={BaseInput}
          disabled={disabled}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (shouldPreventChar?.(e.key)) e.preventDefault();
          }}
          forwardValue={rest.value}
          defaultValue={rest.defaultValue}
          inputRef={(input: HTMLInputElement) => {
            if (nativeInputRef) {
              nativeInputRef.current = input;
            }
          }}
          getInputAPI={(api: InputApi) => getInputAPI?.(api, nativeInputRef)}
          {...opts}
          {...rest}
        />
        {icon}
      </Container>
    );
  };

const getValues = (forwardValue: unknown, forwardDefaultValue: unknown) => {
  const values: { [key: string]: unknown } = {};
  if (forwardValue) {
    values.value = forwardValue;
  }
  if (forwardDefaultValue) {
    values.defaultValue = forwardDefaultValue;
  }
  return values;
};

export const FormattedInputWrapper = IMaskMixin<
  SeturInputProps & {
    InputComponent: React.ComponentType;
    forwardValue?: SeturInputProps['value'];
    forwardDefaultValue?: SeturInputProps['value'];
  },
  unknown
>(
  ({
    InputComponent,
    forwardValue,
    forwardDefaultValue,
    getInputAPI,
    ...rest
  }) => {
    const height = useHeight();
    const labelTop = useLabelTop();
    const values = getValues(forwardValue, forwardDefaultValue);

    const numericInputRef = useRef<InputApi>();
    return (
      <Input
        {...rest}
        {...values}
        InputComponent={InputComponent}
        height={height}
        labelTop={labelTop}
        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
          const value = e.clipboardData.getData('text');
          numericInputRef.current?.applyValue(value);
          rest.onChange?.(value);
        }}
        onInput={(e) => {
          const value = (e.target as HTMLInputElement).value;
          numericInputRef.current?.applyValue(value);
          rest.onChange?.(value);
        }}
        getInputAPI={(numericInputApi: InputApi) => {
          numericInputRef.current = numericInputApi;
          getInputAPI?.(numericInputApi);
        }}
        inputMode='numeric'
      />
    );
  }
);
